import React, { Fragment } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Menu, Transition } from "@headlessui/react";
import { ChevronDownIcon, InboxIcon, PlusIcon } from "@heroicons/react/24/outline";
import { canCreateIn, subjectFolderOptions } from "../../utils/folders";

const focusRing = "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600";
// The page's one main button (FolderBar's size: 44px on phones, next to the 44px "⋯").
const buttonClass = `inline-flex h-11 items-center justify-center gap-1.5 whitespace-nowrap rounded-lg bg-blue-600 px-3.5 text-sm font-medium text-white shadow-sm shadow-blue-600/25 transition duration-200 hover:bg-blue-700 md:h-9 ${focusRing}`;

const createPath = (folderUuid) => (folderUuid ? `/my-questions/create?folder=${folderUuid}` : "/my-questions/create");

/**
 * "Create question" of My Questions. A question is written inside a subject, so it asks where: the subjects
 * whose role creates questions (by folder, from `folders` = GET question_folders/), and "No folder" with the
 * global right (`allowNoFolder`). A single place opens directly; no place, no button. Each subject shows the
 * user's role there only when the roles differ (one role everywhere says nothing per row). `className` goes to
 * the button's wrapper (e.g. "max-md:flex-1": the button fills a phone's row).
 */
export default function CreateQuestionMenu({ folders, allowNoFolder = false, className = "" }) {
  const [t] = useTranslation();
  const groups = subjectFolderOptions(folders, (subject) => canCreateIn(subject.role));
  const places = groups.reduce((sum, group) => sum + group.options.length, 0) + (allowNoFolder ? 1 : 0);
  // Admins have every role, so theirs is never shown.
  const roles = new Set(groups.flatMap((group) => group.options.map((option) => option.folder?.role)).filter((role) => role && role !== "admin"));
  const showRoles = roles.size > 1;

  if (places === 0) return null;
  if (places === 1) {
    const only = groups[0]?.options[0];
    return (
      <Link to={createPath(only?.value)} className={`${buttonClass} ${className}`}>
        <PlusIcon className="h-4 w-4" aria-hidden="true" />
        {t("folders.create_question")}
      </Link>
    );
  }

  return (
    // Phones: the panel spans the caller's actions row (its positioned parent), wherever the button wraps.
    <Menu as="div" className={`relative max-sm:static ${className}`}>
      <Menu.Button className={`${buttonClass} w-full`}>
        <PlusIcon className="h-4 w-4" aria-hidden="true" />
        {t("folders.create_question")}
        <ChevronDownIcon className="h-4 w-4 opacity-80" aria-hidden="true" />
      </Menu.Button>
      <Transition
        as={Fragment}
        enter="transition ease-out duration-100 motion-reduce:transition-none"
        enterFrom="opacity-0 scale-95"
        enterTo="opacity-100 scale-100"
        leave="transition ease-in duration-75 motion-reduce:transition-none"
        leaveFrom="opacity-100 scale-100"
        leaveTo="opacity-0 scale-95"
      >
        <Menu.Items className="absolute z-20 mt-2 flex max-h-[min(28rem,70vh)] origin-top-right flex-col max-sm:inset-x-0 sm:right-0 sm:w-80 rounded-2xl bg-white shadow-xl ring-1 ring-slate-900/10 focus:outline-none">
          <div className="border-b border-slate-100 px-4 pb-2.5 pt-3">
            <p className="text-sm font-semibold text-slate-900">{t("folders.create_menu.title")}</p>
            <p className="mt-0.5 text-xs text-slate-500">{t("folders.create_menu.hint")}</p>
          </div>
          <div className="overflow-y-auto p-1.5">
            {groups.map((group) => (
              <div key={group.label} role="group" aria-label={group.label} className="py-1">
                <p className="truncate px-2.5 pb-1 pt-1.5 text-xs font-semibold text-slate-500" aria-hidden="true">
                  {group.label}
                </p>
                {group.options.map((option) => (
                  <Menu.Item key={option.value}>
                    {({ active }) => (
                      <Link
                        to={createPath(option.value)}
                        className={`flex min-h-[2.75rem] items-center justify-between gap-3 rounded-lg px-2.5 py-2 text-sm md:min-h-0 ${
                          active ? "bg-blue-50 text-blue-800" : "text-slate-800"
                        }`}
                      >
                        <span className="min-w-0 truncate font-medium">{option.label}</span>
                        {showRoles && option.folder?.role && option.folder.role !== "admin" && (
                          <span className="shrink-0 rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600">
                            {t(`folders.roles.${option.folder.role}`, { defaultValue: option.folder.role })}
                          </span>
                        )}
                      </Link>
                    )}
                  </Menu.Item>
                ))}
              </div>
            ))}
            {allowNoFolder && (
              <div className={groups.length ? "mt-1 border-t border-slate-100 pt-1.5" : ""}>
                <Menu.Item>
                  {({ active }) => (
                    <Link
                      to={createPath(null)}
                      className={`flex min-h-[2.75rem] items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm md:min-h-0 ${active ? "bg-blue-50 text-blue-800" : "text-slate-700"}`}
                    >
                      <InboxIcon className="h-4 w-4 shrink-0 text-slate-400" aria-hidden="true" />
                      {t("folders.create_menu.no_folder")}
                    </Link>
                  )}
                </Menu.Item>
              </div>
            )}
          </div>
        </Menu.Items>
      </Transition>
    </Menu>
  );
}
